import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './ProcessingModal.css';
import Header from './Header';
import { BotaoDownloadFormularioPDF } from './FormularioCampoPDF';

const nomenclatureMap = {
  // Pendências
  semPendencias: "Sem pendências",
  fechado: "Domicílio fechado",
  recusa: "Recusa",

  // Situação do Imóvel
  nova: "Nova (primeira pesquisa)",
  reconhecida: "Reconhecida (já foi pesquisada)",
  demolida: "Demolida",

  // Tipos de Parede/Teto (Slugs do App)
  alvenaria_reboco: "Alvenaria c/ reboco",
  alvenaria_sem_reboco: "Alvenaria s/ reboco",
  barro_reboco: "Barro c/ reboco",
  barro_sem_reboco: "Barro s/ reboco",
  madeira: "Madeira",
  taipa: "Taipa",
  palha: "Palha",
  telha: "Telha",
  metalico: "Metálico",
  "alvenaria c/ reboco": "Alvenaria c/ reboco",
  "alvenaria s/ reboco": "Alvenaria s/ reboco",
  "barro c/ reboco": "Barro c/ reboco",
  "barro s/ reboco": "Barro s/ reboco",

  // Vestígios e Outros
  Nenhum: "Nenhum",
  Ovos: "Ovos",
  Urbana: "Urbana",
  Rural: "Rural",
  outros: "Outros",
};

const mapValueToLabel = (value) => {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return nomenclatureMap[value] || (value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : "Não informado");
};

const parseArray = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  if (typeof field === 'string') {
    const trimmed = field.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map(s => s.trim().replace(/^"|"$/g, ''))
        .filter(s => s !== "");
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      return trimmed.split(',').map(s => s.trim()).filter(s => s !== "");
    }
  }
  return [field];
};

const Field = ({ label, children, widthClass = 'w-full' }) => (
  <div className={`sioc-field-wrapper ${widthClass}`}><span className="sioc-label nowrap-label">{label}</span>{children}</div>
);

const NaoInformado = () => <em style={{ color: '#94a3b8', fontStyle: 'italic' }}>Não foi informado</em>;

const DenunciaContextCard = ({ denuncia, showFeedback = false }) => {
  if (!denuncia) return null;

  const dataDenuncia = denuncia.createdAt
    ? new Date(denuncia.createdAt).toLocaleString('pt-BR', { timeZone: 'UTC' })
    : null;

  const enderecoPartes = [denuncia.rua, denuncia.numero].filter(Boolean).join(', ');

  return (
    <div className="denuncia-context-card">
      <h3 className="context-card-title">Contexto da Denúncia</h3>
      {denuncia.fotoPrincipalUrl && (
        <div className="relative" style={{ marginBottom: '15px', borderRadius: '6px', overflow: 'hidden' }}>
          <img
            src={denuncia.fotoPrincipalUrl}
            alt="Foto da denúncia"
            style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', borderRadius: '4px' }}
          />
          <div className="photo-actions-overlay">
            <button className="action-btn zoom" onClick={() => window.open(denuncia.fotoPrincipalUrl, '_blank')} title="Ver foto">🔍</button>
          </div>
        </div>
      )}
      <div className='context-card-grid'>
        <p className="full-width" style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>ID: {denuncia.id || <NaoInformado />}</p>
        <p><strong>Data:</strong> {dataDenuncia || <NaoInformado />}</p>
        <p><strong>Endereço:</strong> {enderecoPartes || <NaoInformado />}</p>
        <p><strong>Bairro:</strong> {denuncia.bairro || <NaoInformado />}</p>
        <p><strong>Complemento:</strong> {denuncia.complemento || <NaoInformado />}</p>
        <p className="full-width"><strong>Descrição:</strong> {denuncia.descricao || <NaoInformado />}</p>
        {showFeedback && denuncia.feedbackAgente && (
          <div className="full-width" style={{ marginTop: '10px', padding: '10px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
            <p style={{ margin: 0, color: '#0369a1', fontSize: '13px' }}><strong>Feedback enviado ao cidadão:</strong></p>
            <p style={{ margin: '4px 0 0 0', fontStyle: 'italic' }}>"{denuncia.feedbackAgente}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ProcessingModal = () => {
  const location = useLocation();
  const initialData = location.state?.data;
  const [isViewMode, setIsViewMode] = useState(initialData && initialData.id && location.state?.mode !== 'process');

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = 'auto'; };
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (isViewMode) {
    return <ProcessingModalView data={initialData} onStartProcess={() => setIsViewMode(false)} />;
  }
  return <ProcessingModalForm initialData={initialData} />;
};

const ProcessingModalForm = ({ initialData }) => {
  const navigate = useNavigate();
  const [toastMsg, setToastMsg] = useState(null);
  const [toastType, setToastType] = useState('info');

  // Fotos do Imóvel
  const [selectedFiles, setSelectedFiles] = useState(Array(4).fill(null));
  const [previews, setPreviews] = useState(Array(4).fill(null));
  const fileInputRefs = useRef([]);

  // Fotos da Ficha Física
  const [selectedFichaFiles, setSelectedFichaFiles] = useState(Array(2).fill(null));
  const [fichaPreviews, setFichaPreviews] = useState(Array(2).fill(null));
  const fichaInputRefs = useRef([]);

  const [agentes, setAgentes] = useState([]);
  const [isLoadingAgentes, setIsLoadingAgentes] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [feedbackAgente, setFeedbackAgente] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationMessage, setLocationMessage] = useState(null);
  const [municipioNome, setMunicipioNome] = useState('');
  const [denunciaData, setDenunciaData] = useState(null);

  const isAttendingDenuncia = initialData && initialData.denuncia_id && !initialData.id;
  const isUpdating = initialData && initialData.id;
  const returnRoute = isAttendingDenuncia ? '/vigilancia-passiva' : '/formularios';

  const [formData, setFormData] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const baseData = {
      data_atividade: today,
      tipo_atividade: ['pesquisa'],
      pendencia_pesquisa: 'semPendencias',
      pendencia_borrifacao: 'semPendencias',
      situacao_imovel: 'reconhecida',
      melhoria_habitacional: null,
      barbeiros_intradomicilio: 0,
      vestigios_intradomicilio: 'Nenhum',
      barbeiros_peridomicilio: 0,
      vestigios_peridomicilio: 'Nenhum',
      numero_cargas: 0,
      numero_anexo: 0,
      codigo_localidade: '',
      nome_localidade: '',
      categoria_localidade: 'Urbana',
      fotos_urls: [],
      fotos_ficha_urls: [],
      agente_id: '',
      denuncia_id: null,
      numero_pit: '',
      latitude: null,
      longitude: null,
      endereco: '',
      numero: '',
      complemento: '',
      nome_morador: '',
      quantidade_moradores: 0,
    };

    if (isUpdating) {
      return {
        ...baseData,
        ...initialData,
        tipo_atividade: parseArray(initialData.tipo_atividade),
        fotos_urls: parseArray(initialData.fotos_urls),
        fotos_ficha_urls: parseArray(initialData.fotos_ficha_urls),
        quantidade_moradores: initialData.quantidade_moradores ?? 0,
        numero_anexo: initialData.numero_anexo ?? 0
      };
    }

    if (isAttendingDenuncia) {
      return {
        ...baseData,
        denuncia_id: initialData.denuncia_id,
        endereco: initialData.denuncia_rua || '',
        numero: initialData.denuncia_numero || '',
        complemento: initialData.denuncia_complemento || '',
      };
    }
    return baseData;
  });

  useEffect(() => {
    const fetchRealData = async () => {
      if (initialData && initialData.id) {
        try {
          const { data, error } = await supabase
            .from('ocorrencias')
            .select('quantidade_moradores, categoria_localidade, numero_anexo, fotos_ficha_urls, melhoria_habitacional')
            .eq('id', initialData.id)
            .single();

          if (data && !error) {
            setFormData(prev => ({
              ...prev,
              quantidade_moradores: data.quantidade_moradores ?? prev.quantidade_moradores,
              categoria_localidade: data.categoria_localidade || prev.categoria_localidade,
              numero_anexo: data.numero_anexo ?? prev.numero_anexo,
              fotos_ficha_urls: parseArray(data.fotos_ficha_urls),
              melhoria_habitacional: data.melhoria_habitacional
            }));
          }
        } catch (e) {
          console.error("Erro ao carregar dados reais da ocorrência:", e);
        }
      }
    };
    fetchRealData();
  }, [initialData]);

  // Busca os dados da denúncia diretamente da tabela denuncias
  useEffect(() => {
    const fetchDenuncia = async () => {
      const denId = initialData?.denuncia_id || formData.denuncia_id;
      if (!denId) return;
      const { data } = await supabase.from('denuncias').select('*').eq('id', denId).single();
      if (data) {
        setDenunciaData({
          id: data.id,
          rua: data.rua,
          numero: data.numero,
          bairro: data.bairro,
          complemento: data.complemento,
          descricao: data.descricao,
          createdAt: data.created_at,
          fotoPrincipalUrl: data.foto_url,
        });
        // Remove a foto da denúncia de fotos_urls — pertence ao contexto da denúncia,
        // não ao registro do atendimento feito pelo agente
        setFormData(prev => ({
          ...prev,
          fotos_urls: data.foto_url
            ? prev.fotos_urls.filter(url => url !== data.foto_url)
            : prev.fotos_urls,
          // Pré-preenche endereço com dados da denúncia ao atender uma nova denúncia
          ...(isAttendingDenuncia && {
            endereco: prev.endereco || data.rua || '',
            numero: prev.numero || data.numero || '',
            complemento: prev.complemento || data.complemento || '',
          }),
        }));
        // Pré-carrega o feedback já salvo ao editar/processar
        if (data.feedback_agente) {
          setFeedbackAgente(data.feedback_agente);
        }
      }
    };
    fetchDenuncia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showIntraCaptureInput, setShowIntraCaptureInput] = useState(formData.barbeiros_intradomicilio > 0);
  const [showPeriCaptureInput, setShowPeriCaptureInput] = useState(formData.barbeiros_peridomicilio > 0);

  useEffect(() => {
    const fetchAgentesAndUser = async () => {
      setIsLoadingAgentes(true);
      const municipioId = localStorage.getItem('user_municipio_id');
      if (!municipioId) {
        showToast("Erro: Município não identificado.", "error");
        setIsLoadingAgentes(false);
        return;
      }

      const { data: agentesData, error: agentesError } = await supabase
        .rpc('get_agentes_by_municipio', { p_municipio_id: municipioId });

      if (agentesError) {
        showToast('Não foi possível carregar a lista de agentes.', "error");
      } else {
        setAgentes(agentesData || []);
      }

      const { data: mData } = await supabase.from('municipios').select('nome').eq('id', municipioId).single();
      if (mData) setMunicipioNome(mData.nome);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const role = user.app_metadata?.role;
        setUserRole(role);
        if (role !== 'admin' && !isUpdating) {
          setFormData(prev => ({ ...prev, agente_id: user.id }));
        }
      }
      setIsLoadingAgentes(false);
    };
    fetchAgentesAndUser();
  }, [isUpdating]);

  const showToast = (msg, type = 'info') => {
    setToastMsg(msg);
    setToastType(type);
    if (type !== 'error') {
      setTimeout(() => setToastMsg(null), 4000);
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocalização não é suportada pelo seu navegador.");
      return;
    }
    setIsGettingLocation(true);
    setLocationMessage("Obtendo GPS...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({ ...prev, latitude, longitude }));
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`);
          const data = await response.json();
          if (data && data.address) {
            const street = data.address.road || data.address.pedestrian || data.address.suburb || "";
            const number = data.address.house_number || "";
            setFormData(prev => ({
              ...prev,
              endereco: street || prev.endereco,
              numero: number || prev.numero
            }));
            setLocationMessage("Localização e endereço atualizados!");
          } else {
            setLocationMessage("Coordenadas GPS capturadas! Endereço não encontrado.");
          }
        } catch (error) {
          console.error("Erro no geocoding:", error);
          setLocationMessage("Coordenadas GPS capturadas! Não foi possível obter o endereço automaticamente.");
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        console.error("Erro ao obter localização:", error);
        let errorMsg = "Erro ao obter GPS";
        if (error.code === 1) errorMsg = "Erro: Permissão de localização negada pelo usuário.";
        else if (error.code === 2) errorMsg = "Erro: Localização indisponível. Verifique se o GPS do sistema está ativado.";
        else if (error.code === 3) errorMsg = "Erro: Tempo esgotado ao obter localização.";
        setLocationMessage(errorMsg);
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleFileChange = (e, index, isFicha = false) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isFicha) {
        const newFiles = [...selectedFichaFiles];
        newFiles[index] = file;
        setSelectedFichaFiles(newFiles);
        const newPreviews = [...fichaPreviews];
        newPreviews[index] = URL.createObjectURL(file);
        setFichaPreviews(newPreviews);
      } else {
        const newFiles = [...selectedFiles];
        newFiles[index] = file;
        setSelectedFiles(newFiles);
        const newPreviews = [...previews];
        newPreviews[index] = URL.createObjectURL(file);
        setPreviews(newPreviews);
      }
    }
  };

  const removePhoto = (idx, isFicha = false) => {
    if (isFicha) {
        setFormData(prev => ({ ...prev, fotos_ficha_urls: prev.fotos_ficha_urls.filter((_, i) => i !== idx) }));
    } else {
        setFormData(prev => ({ ...prev, fotos_urls: prev.fotos_urls.filter((_, i) => i !== idx) }));
    }
  };

  const triggerFileInput = (index, isFicha = false) => {
    if (isFicha) {
      if (fichaInputRefs.current[index]) fichaInputRefs.current[index].click();
    } else {
      if (fileInputRefs.current[index]) fileInputRefs.current[index].click();
    }
  };

  const uploadFiles = async (files, recordId, bucket = 'fotos-ocorrencias') => {
    const urls = [];
    for (const [index, file] of files.entries()) {
      if (!file) continue;
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `${recordId}/${Date.now()}_${index}.${fileExtension}`;
      const { error } = await supabase.storage
        .from(bucket)
        .upload(uniqueFileName, file);
      if (error) continue;
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(uniqueFileName);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : (type === 'number' && value !== '' ? parseInt(value, 10) : value);
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleMelhoriaChange = (value) => {
    setFormData(prev => ({ ...prev, melhoria_habitacional: prev.melhoria_habitacional === value ? null : value }));
  }

  const handleActivityChange = (activity) => {
    setFormData(prev => {
      const currentActivities = prev.tipo_atividade || [];
      const newActivities = currentActivities.includes(activity)
        ? currentActivities.filter(a => a !== activity)
        : [...currentActivities, activity];
      return { ...prev, tipo_atividade: newActivities };
    });
  };

  const handleVestigioChange = (field, value, checked) => {
    setFormData(prev => {
      let current = String(prev[field] || 'Nenhum');
      let parts = current === 'Nenhum' ? [] : current.split(', ').filter(s => s !== "");

      if (value === 'Nenhum') {
        return { ...prev, [field]: checked ? 'Nenhum' : 'Nenhum' };
      } else {
        if (checked) {
          if (!parts.includes(value)) parts.push(value);
        } else {
          parts = parts.filter(p => p !== value);
        }
        const newValue = parts.length === 0 ? 'Nenhum' : parts.join(', ');
        return { ...prev, [field]: newValue };
      }
    });
  };

  const handleCaptureStatusChange = (field, status, setShowInput) => {
    setShowInput(status === 'Triatomíneo');
    if (status === 'Nenhum') {
      setFormData(prev => ({ ...prev, [field]: 0 }));
    } else {
      setFormData(prev => ({ ...prev, [field]: prev[field] > 0 ? prev[field] : 1 }));
    }
  };

  const handleSave = async () => {
    if (!formData.agente_id) {
      showToast("Adicione o agente responsável por esse formulário", "warning");
      return;
    }
    if (!formData.data_atividade) {
      showToast("Atenção: A Data da Atividade é obrigatória.", "warning");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não identificado.");

      const dataToSubmit = {
        ...formData,
        barbeiros_intradomicilio: formData.barbeiros_intradomicilio || 0,
        barbeiros_peridomicilio: formData.barbeiros_peridomicilio || 0,
        sincronizado: isUpdating ? true : false,
        municipio_id: localStorage.getItem('user_municipio_id'),
        feedback_agente: feedbackAgente || null,
      };

      const viewOnlyFields = [
        'localidade', 'agente_nome', 'exame_vinculado', 'status_sincronizacao',
        'ocorrencia_data_registro', 'nome_agente_view', 'nome_localidade_view',
        'categoria_localidade_view', 'status_exame', 'data', 'data_exame',
        'tecnico_responsavel', 'situacao', 'exame_id', 'vestigios_intra', 'vestigios_peri',
        'caso_id', 'origem_status', 'ai_suggestion', 'foto_principal'
      ];
      viewOnlyFields.forEach(field => delete dataToSubmit[field]);

      const { data, error } = await supabase.from('ocorrencias').upsert([dataToSubmit]).select();
      if (error) throw error;

      const recordId = data[0].id;
      
      // Upload Fotos Imóvel
      const filesToUpload = selectedFiles.filter(f => f);
      const urls = await uploadFiles(filesToUpload, recordId);
      
      // Upload Fotos Ficha
      const fichaFilesToUpload = selectedFichaFiles.filter(f => f);
      const fichaUrls = await uploadFiles(fichaFilesToUpload, recordId);

      if (urls.length > 0 || fichaUrls.length > 0) {
        const updatePayload = {};
        if (urls.length > 0) updatePayload.fotos_urls = [...formData.fotos_urls, ...urls];
        if (fichaUrls.length > 0) updatePayload.fotos_ficha_urls = [...formData.fotos_ficha_urls, ...fichaUrls];
        
        await supabase.from('ocorrencias').update(updatePayload).eq('id', recordId);
      }

      if (dataToSubmit.denuncia_id) {
        await supabase.from('denuncias').update({
          status: 'atendida',
          feedback_agente: feedbackAgente || null
        }).eq('id', dataToSubmit.denuncia_id);
      }

      showToast(isUpdating ? "Formulário processado com sucesso!" : "Registro criado com sucesso!", "success");
      setTimeout(() => navigate(returnRoute, { replace: true }), 1500);
    } catch (error) {
      showToast(`Erro ao salvar: ${error.message}`, "error");
    }
  };

  const onClose = () => navigate(returnRoute);

  const remainingSlots = 4 - (formData.fotos_urls?.length || 0);

  const RegistrationSection = (
    <div className="photos-area">
      <h3 className="photos-area-title">Registros do Atendimento</h3>
      <div className="photos-grid-2col">
        {formData.fotos_urls.map((url, idx) => (
          <div key={`saved-${idx}`} className="photo-card-small relative">
            <img src={url} alt={`Existente ${idx + 1}`} />
            <div className="photo-actions-overlay">
                <button className="action-btn zoom" onClick={() => window.open(url, '_blank')} title="Ver foto">🔍</button>
                <button className="action-btn delete" onClick={() => removePhoto(idx)} title="Remover para trocar">✕</button>
            </div>
            <span className="photo-label">Salva</span>
          </div>
        ))}
        {remainingSlots > 0 && previews.slice(0, remainingSlots).map((preview, index) => (
          <div key={`new-${index}`} className="photo-upload-card" onClick={() => triggerFileInput(index)}>
            <input type="file" ref={el => fileInputRefs.current[index] = el} onChange={(e) => handleFileChange(e, index)} accept="image/*" style={{ display: 'none' }} />
            {preview ? (
              <>
                <img src={preview} alt={`Preview ${index + 1}`} />
                <div className="photo-actions-overlay">
                    <button className="action-btn zoom" onClick={(e) => { e.stopPropagation(); window.open(preview, '_blank'); }} title="Ver foto">🔍</button>
                    <button className="action-btn change" onClick={(e) => { e.stopPropagation(); triggerFileInput(index); }} title="Trocar imagem">↻</button>
                </div>
              </>
            ) : <div className="upload-placeholder"><span>+</span></div>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="processing-page" style={{ background: '#ffffff' }}>
      <Header />
      <main className="processing-main-content">
        <div style={{ width: '100%', maxWidth: '1400px' }}>
          <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{isUpdating ? 'Processar Formulário de Campo' : 'Formulário de Campo'}</h1>
              {(isAttendingDenuncia || formData.denuncia_id) && (
                <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748b' }}>Atendimento à Denúncia</span>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: '32px', cursor: 'pointer' }} title="Fechar">&times;</button>
          </div>
          <div className={`page-content-wrapper ${isAttendingDenuncia || isUpdating ? 'layout-denuncia' : ''}`}>
            {(isAttendingDenuncia || isUpdating) ? (
              <>
                <div className="form-main-column">
                  <FormFields
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleActivityChange={handleActivityChange}
                    handleMelhoriaChange={handleMelhoriaChange}
                    handleVestigioChange={handleVestigioChange}
                    handleCaptureStatusChange={handleCaptureStatusChange}
                    showIntraCaptureInput={showIntraCaptureInput}
                    showPeriCaptureInput={showPeriCaptureInput}
                    setShowIntraCaptureInput={setShowIntraCaptureInput}
                    setShowPeriCaptureInput={setShowPeriCaptureInput}
                    agentes={agentes}
                    isLoadingAgentes={isLoadingAgentes}
                    userRole={userRole}
                    onGetLocation={handleGetLocation}
                    isGettingLocation={isGettingLocation}
                    locationMessage={locationMessage}
                    selectedFichaFiles={selectedFichaFiles}
                    fichaPreviews={fichaPreviews}
                    fichaInputRefs={fichaInputRefs}
                    handleFileChange={handleFileChange}
                    triggerFileInput={triggerFileInput}
                    removePhoto={removePhoto}
                  />
                </div>
                <div className="denuncia-context-panel">
                  <DenunciaContextCard denuncia={denunciaData} />
                </div>
                <div className="denuncia-actions-panel">
                  {(isAttendingDenuncia || denunciaData) && (
                    <div className="feedback-section-card" style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', color: '#1e293b' }}>Mensagem para o Cidadão</h3>
                      <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Esta mensagem aparecerá no app do morador confirmando a conclusão do atendimento.</p>
                      <textarea placeholder="Ex: Foco tratado; Não era um barbeiro..." value={feedbackAgente} onChange={(e) => setFeedbackAgente(e.target.value)} style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                    </div>
                  )}
                  {RegistrationSection}
                </div>
              </>
            ) : (
              <>
                <div className="form-left-column-single">{RegistrationSection}</div>
                <div className="form-right-column">
                  <FormFields
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleActivityChange={handleActivityChange}
                    handleMelhoriaChange={handleMelhoriaChange}
                    handleVestigioChange={handleVestigioChange}
                    handleCaptureStatusChange={handleCaptureStatusChange}
                    showIntraCaptureInput={showIntraCaptureInput}
                    showPeriCaptureInput={showPeriCaptureInput}
                    setShowIntraCaptureInput={setShowIntraCaptureInput}
                    setShowPeriCaptureInput={setShowPeriCaptureInput}
                    agentes={agentes}
                    isLoadingAgentes={isLoadingAgentes}
                    userRole={userRole}
                    onGetLocation={handleGetLocation}
                    isGettingLocation={isGettingLocation}
                    locationMessage={locationMessage}
                    selectedFichaFiles={selectedFichaFiles}
                    fichaPreviews={fichaPreviews}
                    fichaInputRefs={fichaInputRefs}
                    handleFileChange={handleFileChange}
                    triggerFileInput={triggerFileInput}
                    removePhoto={removePhoto}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {toastMsg && (
          <div className={`toast-card ${toastType}`} onClick={() => setToastMsg(null)}>
            <div className="toast-icon">
              {toastType === 'error' && '❌'}
              {toastType === 'warning' && '⚠️'}
              {toastType === 'success' && '✅'}
              {toastType === 'info' && 'ℹ️'}
            </div>
            <div className="toast-content">
              <p className="toast-text">{toastMsg}</p>
              {toastType === 'error' && <p className="toast-subtext">Clique para fechar</p>}
            </div>
          </div>
        )}
      </main>
      <div className="portal-footer-bar">
        <button className="btn-cancel" onClick={onClose}>Cancelar</button>
        <div style={{ marginLeft: 'auto', marginRight: '12px' }}>
          <BotaoDownloadFormularioPDF
            data={formData}
            agenteNome={agentes.find(a => a.id === formData.agente_id)?.nome_completo || ''}
            municipioNome={municipioNome}
            fotosUrls={parseArray(formData.fotos_urls)}
            fotoDenunciaUrl={denunciaData?.fotoPrincipalUrl || null}
            denunciaData={denunciaData}
            feedbackAgente={feedbackAgente}
            fichaFotosUrls={parseArray(formData.fotos_ficha_urls)}
            isReady={!isLoadingAgentes && municipioNome !== ''}
          />
        </div>
        <button className="btn-save" onClick={handleSave}>{isUpdating ? 'Concluir e Sincronizar' : 'Criar Registro'}</button>
      </div>
      <style>{`
        .relative { position: relative; }
        .photo-actions-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.2); display: flex; justify-content: center; align-items: center; gap: 10px; opacity: 0; transition: opacity 0.2s; border-radius: 4px; }
        .photo-card-small:hover .photo-actions-overlay, .photo-upload-card:hover .photo-actions-overlay, .photo-card-mini:hover .photo-actions-overlay, .photo-upload-mini:hover .photo-actions-overlay { opacity: 1; }
        .action-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: white; cursor: pointer; display: flex; justify-content: center; align-items: center; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); transition: transform 0.2s; }
        .action-btn:hover { transform: scale(1.1); }
        .action-btn.delete { color: #ef4444; }
        .action-btn.zoom { color: #3b82f6; }
        .action-btn.change { color: #10b981; }
      `}</style>
    </div>
  );
}

const FormFields = ({
  formData,
  setFormData,
  handleChange,
  handleActivityChange,
  handleMelhoriaChange,
  handleVestigioChange,
  handleCaptureStatusChange,
  showIntraCaptureInput,
  showPeriCaptureInput,
  setShowIntraCaptureInput,
  setShowPeriCaptureInput,
  agentes,
  isLoadingAgentes,
  userRole,
  onGetLocation,
  isGettingLocation,
  locationMessage,
  selectedFichaFiles,
  fichaPreviews,
  fichaInputRefs,
  handleFileChange,
  triggerFileInput,
  removePhoto
}) => {
  const isLocationError = locationMessage && locationMessage.includes('Erro');
  const remainingFichaSlots = 2 - (formData.fotos_ficha_urls?.length || 0);

  return (
    <div className="form-content-area">
      <div className="sioc-group-title" style={{ marginTop: 0 }}>Dados da Atividade</div>
      <div className="form-stack">
        <Field label="Tipo de Atividade">
          <div className="checkbox-group-clean">
            <label className="checkbox-item"><input type="checkbox" checked={formData.tipo_atividade.includes('pesquisa')} onChange={() => handleActivityChange('pesquisa')} /> Pesquisa</label>
            <label className="checkbox-item"><input type="checkbox" checked={formData.tipo_atividade.includes('borrifacao')} onChange={() => handleActivityChange('borrifacao')} /> Borrifação</label>
            <label className="checkbox-item"><input type="checkbox" checked={formData.tipo_atividade.includes('atendimentoPIT')} onChange={() => handleActivityChange('atendimentoPIT')} /> Atendimento ao PIT</label>
          </div>
        </Field>
        <Field label="Data da Atividade"><input type="date" name="data_atividade" value={formData.data_atividade || ''} onChange={handleChange} className="sioc-input-box w-auto" /></Field>
        <Field label="Número do PIT" widthClass="w-medium"><input type="text" name="numero_pit" value={formData.numero_pit || ''} onChange={handleChange} className="sioc-input-box" /></Field>
      </div>

      <div className="sioc-group-title">Dados do Domicílio</div>
      <div style={{ marginBottom: '20px' }}>
        <button type="button" onClick={onGetLocation} disabled={isGettingLocation} className="btn-location-gps">
          {isGettingLocation ? <div className="spinner-small" /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>}
          {isGettingLocation ? 'Obtendo GPS...' : 'Obter Localização'}
        </button>
        {locationMessage && <div className={`location-status-msg ${isLocationError ? 'error' : 'success'}`}>{locationMessage}</div>}
      </div>

      <div className="form-stack">
        <Field label="Código da Localidade" widthClass="w-medium"><input type="text" name="codigo_localidade" value={formData.codigo_localidade || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Nome da Localidade"><input type="text" name="nome_localidade" value={formData.nome_localidade || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Categoria" widthClass="w-medium"><input type="text" name="categoria_localidade" value={formData.categoria_localidade || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Endereço"><input type="text" name="endereco" value={formData.endereco || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Número" widthClass="w-auto"><input type="text" name="numero" value={formData.numero || ''} onChange={handleChange} className="sioc-input-box" style={{ width: '100px' }} /></Field>
        <Field label="Complemento"><input type="text" name="complemento" value={formData.complemento || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Pendência da Pesquisa" widthClass="w-medium"><select name="pendencia_pesquisa" value={formData.pendencia_pesquisa || ''} onChange={handleChange} className="sioc-input-box"><option value="semPendencias">Sem pendências</option><option value="fechado">Domicílio fechado</option><option value="recusa">Recusa</option></select></Field>
        <Field label="Pendência da Borrifação" widthClass="w-medium"><select name="pendencia_borrifacao" value={formData.pendencia_borrifacao || ''} onChange={handleChange} className="sioc-input-box"><option value="semPendencias">Sem pendências</option><option value="fechado">Domicílio fechado</option><option value="recusa">Recusa</option></select></Field>
        <Field label="Nome do Morador"><input type="text" name="nome_morador" value={formData.nome_morador || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Quantidade de Moradores" widthClass="w-auto"><input type="number" name="quantidade_moradores" value={formData.quantidade_moradores ?? 0} onChange={handleChange} className="sioc-input-box" style={{ width: '100px' }} /></Field>
        
        <Field label="Número Anexo*" widthClass="w-full">
          <div className="choice-chips-container">
            {[0, 1, 2, 3, 4, 5].map(val => (
              <button key={val} type="button" className={`choice-chip ${formData.numero_anexo === val ? 'active' : ''}`} onClick={() => setFormData(prev => ({...prev, numero_anexo: val}))}>{val}</button>
            ))}
          </div>
        </Field>

        <Field label="Situação do Imóvel" widthClass="w-medium"><select name="situacao_imovel" value={formData.situacao_imovel || ''} onChange={handleChange} className="sioc-input-box"><option value="reconhecida">Reconhecida (já foi pesquisada)</option><option value="nova">Nova (primeira pesquisa)</option><option value="demolida">Demolida</option></select></Field>
        <Field label="Tipo de Parede" widthClass="w-medium"><select name="tipo_parede" value={formData.tipo_parede || ''} onChange={handleChange} className="sioc-input-box"><option value="">Selecione</option><option value="alvenaria_reboco">Alvenaria c/ reboco</option><option value="alvenaria_sem_reboco">Alvenaria s/ reboco</option><option value="barro_reboco">Barro c/ reboco</option><option value="barro_sem_reboco">Barro s/ reboco</option><option value="madeira">Madeira</option><option value="taipa">Taipa</option><option value="palha">Palha</option><option value="outros">Outros</option></select></Field>
        <Field label="Tipo de Teto" widthClass="w-medium"><select name="tipo_teto" value={formData.tipo_teto || ''} onChange={handleChange} className="sioc-input-box"><option value="">Selecione</option><option value="telha">Telha</option><option value="metalico">Metálico</option><option value="madeira">Madeira</option><option value="palha">Palha</option><option value="outros">Outros</option></select></Field>
        <Field label="Melhoria Habitacional">
          <div className="checkbox-group-clean">
            <label className="checkbox-item"><input type="radio" checked={formData.melhoria_habitacional === true} onChange={() => handleMelhoriaChange(true)} /> Sim</label>
            <label className="checkbox-item"><input type="radio" checked={formData.melhoria_habitacional === false} onChange={() => handleMelhoriaChange(false)} /> Não</label>
          </div>
        </Field>
      </div>

      <div className="sioc-group-title">Captura Intradomicílio</div>
      <div className="form-stack">
        <Field label="Captura" widthClass="w-medium"><select value={showIntraCaptureInput ? 'Triatomíneo' : 'Nenhum'} onChange={(e) => handleCaptureStatusChange('barbeiros_intradomicilio', e.target.value, setShowIntraCaptureInput)} className="sioc-input-box"><option>Nenhum</option><option>Triatomíneo</option></select></Field>
        <Field label="Vestígios" widthClass="w-medium">
          <div className="checkbox-group-clean">
            <label className="checkbox-item"><input type="checkbox" checked={formData.vestigios_intradomicilio === 'Nenhum'} onChange={(e) => handleVestigioChange('vestigios_intradomicilio', 'Nenhum', e.target.checked)} /> Nenhum</label>
            <label className="checkbox-item"><input type="checkbox" checked={String(formData.vestigios_intradomicilio).includes('Ovos')} onChange={(e) => handleVestigioChange('vestigios_intradomicilio', 'Ovos', e.target.checked)} /> Ovos</label>
          </div>
        </Field>
        {showIntraCaptureInput && (<Field label="Número de Barbeiros Capturados" widthClass="w-auto"><input type="number" min="1" name="barbeiros_intradomicilio" value={formData.barbeiros_intradomicilio} onChange={handleChange} className="sioc-input-box" style={{ width: '100px' }} /></Field>)}
      </div>

      <div className="sioc-group-title">Captura Peridomicílio</div>
      <div className="form-stack">
        <Field label="Captura" widthClass="w-medium"><select value={showPeriCaptureInput ? 'Triatomíneo' : 'Nenhum'} onChange={(e) => handleCaptureStatusChange('barbeiros_peridomicilio', e.target.value, setShowPeriCaptureInput)} className="sioc-input-box"><option>Nenhum</option><option>Triatomíneo</option></select></Field>
        <Field label="Vestígios" widthClass="w-medium">
          <div className="checkbox-group-clean">
            <label className="checkbox-item"><input type="checkbox" checked={formData.vestigios_peridomicilio === 'Nenhum'} onChange={(e) => handleVestigioChange('vestigios_peridomicilio', 'Nenhum', e.target.checked)} /> Nenhum</label>
            <label className="checkbox-item"><input type="checkbox" checked={String(formData.vestigios_peridomicilio).includes('Ovos')} onChange={(e) => handleVestigioChange('vestigios_peridomicilio', 'Ovos', e.target.checked)} /> Ovos</label>
          </div>
        </Field>
        {showPeriCaptureInput && (<Field label="Número de Barbeiros Capturados" widthClass="w-auto"><input type="number" min="1" name="barbeiros_peridomicilio" value={formData.barbeiros_peridomicilio} onChange={handleChange} className="sioc-input-box" style={{ width: '100px' }} /></Field>)}
      </div>

      <div className="sioc-group-title">Borrifação</div>
      <div className="form-stack">
        <Field label="Inseticida" widthClass="w-large"><input type="text" name="inseticida" value={formData.inseticida || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Número de Cargas" widthClass="w-full">
          <div className="choice-chips-container">
            {[0, 1, 2, 3, 4, 5].map(val => (
              <button key={val} type="button" className={`choice-chip ${Number(formData.numero_cargas) === val ? 'active' : ''}`} onClick={() => setFormData(prev => ({...prev, numero_cargas: val}))}>{val}</button>
            ))}
          </div>
        </Field>
      </div>

      <div className="sioc-group-title">Dados de Identificação</div>
      <div className="form-stack">
        <Field label="Código de Etiqueta" widthClass="w-large"><input type="text" name="codigo_etiqueta" value={formData.codigo_etiqueta || ''} onChange={handleChange} className="sioc-input-box" /></Field>
        <Field label="Agente Responsável" widthClass="w-large"><select name="agente_id" value={formData.agente_id} onChange={handleChange} className="sioc-input-box" disabled={isLoadingAgentes || (userRole && userRole !== 'admin')}><option value="">{isLoadingAgentes ? 'Carregando...' : 'Selecione um agente'}</option>{agentes.map(agente => (<option key={agente.id} value={agente.id}>{agente.nome_completo}</option>))}</select></Field>
      </div>

      <div className="sioc-group-title">Anexo da Ficha Física (Opcional)</div>
      <div className="ficha-upload-section">
        <p className="ficha-upload-instruction">Anexe fotos da ficha física preenchida para backup e conferência.</p>
        <div className="photos-grid-2col-mini">
          {formData.fotos_ficha_urls.map((url, idx) => (
             <div key={`saved-ficha-${idx}`} className="photo-card-mini relative">
                <img src={url} alt={`Ficha salva ${idx + 1}`} />
                <div className="photo-actions-overlay">
                    <button className="action-btn zoom" onClick={() => window.open(url, '_blank')} title="Ver foto">🔍</button>
                    <button className="action-btn delete" onClick={() => removePhoto(idx, true)} title="Remover">✕</button>
                </div>
                <span className="photo-label">Salva</span>
             </div>
          ))}
          {remainingFichaSlots > 0 && fichaPreviews.slice(0, remainingFichaSlots).map((preview, index) => (
            <div key={`preview-ficha-${index}`} className="photo-upload-mini" onClick={() => triggerFileInput(index, true)}>
              <input type="file" ref={el => fichaInputRefs.current[index] = el} onChange={(e) => handleFileChange(e, index, true)} accept="image/*" style={{ display: 'none' }} />
              {preview ? (
                <>
                  <img src={preview} alt={`Ficha Preview ${index + 1}`} />
                  <div className="photo-actions-overlay">
                    <button className="action-btn zoom" onClick={(e) => { e.stopPropagation(); window.open(preview, '_blank'); }} title="Ver foto">🔍</button>
                    <button className="action-btn change" onClick={(e) => { e.stopPropagation(); triggerFileInput(index, true); }} title="Trocar">↻</button>
                  </div>
                </>
              ) : <div className="upload-placeholder-mini"><span>+ Ficha</span></div>}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .spinner-small { width: 16px; height: 16px; border: 2px solid #94a3b8; border-top-color: #334155; border-radius: 50%; animation: spin 1s linear infinite; }
        .btn-location-gps { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background: white; border: 1px solid #cbd5e1; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 600; color: #334155; }
        .location-status-msg { margin-top: 10px; padding: 8px 12px; border-radius: 6px; font-size: 13px; }
        .location-status-msg.success { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; }
        .location-status-msg.error { background: #fef2f2; color: #991b1b; border: 1px solid #fee2e2; }
        .choice-chips-container { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
        .choice-chip { padding: 8px 16px; border: 1px solid #cbd5e1; border-radius: 20px; background: white; cursor: pointer; transition: all 0.2s; font-size: 14px; }
        .choice-chip.active { background: #0f172a; color: white; border-color: #0f172a; }
        .ficha-upload-section { margin-top: 10px; }
        .ficha-upload-instruction { font-size: 12px; color: #64748b; margin-bottom: 12px; }
        
        /* Mini uploads para a ficha */
        .photos-grid-2col-mini { display: flex; gap: 12px; }
        .photo-card-mini { width: 120px; height: 120px; border: 1px solid #eee; padding: 4px; border-radius: 8px; background: white; }
        .photo-card-mini img { width: 100%; height: 100%; object-fit: cover; border-radius: 4px; }
        .photo-upload-mini { width: 120px; height: 120px; border: 2px dashed #e2e8f0; border-radius: 8px; display: flex; justify-content: center; align-items: center; cursor: pointer; background-color: #f8fafc; position: relative; }
        .upload-placeholder-mini span { font-size: 14px; color: #94a3b8; font-weight: 600; }
        .photo-upload-mini img { width: 100%; height: 100%; object-fit: cover; border-radius: 6px; }
      `}</style>
    </div>
  );
}

const ProcessingModalView = ({ data, onStartProcess }) => {
  const navigate = useNavigate();
  const [toastMsg, setToastMsg] = useState(null);
  const returnRoute = data?.denuncia_id ? '/vigilancia-passiva' : '/formularios';
  const [agenteNome, setAgenteNome] = useState('Carregando...');
  const [municipioNome, setMunicipioNome] = useState('');
  const [denuncia, setDenuncia] = useState(null);

  useEffect(() => {
    if (!data) { navigate(returnRoute); return; }
    const fetchRelatedData = async () => {
      if (data.agente_id) {
        const { data: agenteData } = await supabase.from('agentes').select('nome').eq('id', data.agente_id).single();
        setAgenteNome(agenteData ? agenteData.nome : "Não encontrado");
      }
      if (data.denuncia_id) {
        const { data: dData } = await supabase.from('denuncias').select('*').eq('id', data.denuncia_id).single();
        if (dData) setDenuncia({...dData, fotoPrincipalUrl: dData.foto_url, createdAt: dData.created_at, feedbackAgente: dData.feedback_agente });
      }
      const municipioId = data.municipio_id || localStorage.getItem('user_municipio_id');
      if (municipioId) {
        const { data: mData } = await supabase.from('municipios').select('nome').eq('id', municipioId).single();
        if (mData) setMunicipioNome(mData.nome);
      }
    };
    fetchRelatedData();
  }, [data, navigate, returnRoute]);

  if (!data) return null;
  // Filtra a foto da denúncia para que não apareça em "Registros do Atendimento"
  const images = parseArray(data.fotos_urls).filter(url => url !== denuncia?.fotoPrincipalUrl);
  const fichaImages = parseArray(data.fotos_ficha_urls);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    setToastMsg("Copiado!");
    setTimeout(() => setToastMsg(null), 1500);
  };

  const onClose = () => navigate(returnRoute);
  const dateObj = data.data_atividade ? new Date(data.data_atividade) : null;
  const day = dateObj ? String(dateObj.getUTCDate()).padStart(2, '0') : '';
  const month = dateObj ? dateObj.toLocaleString('pt-BR', { month: 'long', timeZone: 'UTC' }) : '';
  const year = dateObj ? dateObj.getUTCFullYear() : '';

  const activityList = parseArray(data.tipo_atividade).map(item => String(item).toLowerCase());
  const isPesquisa = activityList.includes('pesquisa');
  const isBorrifacao = activityList.includes('borrifacao') || activityList.includes('borrifação');
  const isPit = activityList.includes('atendimentopit');

  const capturaIntra = (data.barbeiros_intradomicilio > 0) ? "Triatomíneo" : "Nenhum";
  const capturaPeri = (data.barbeiros_peridomicilio > 0) ? "Triatomíneo" : "Nenhum";

  const InputSioc = ({ label, value, widthClass = "w-full", customInputWidth, allowCopy = true }) => (
    <div className={`sioc-field-wrapper ${widthClass}`}>
      <span className="sioc-label nowrap-label">{label}</span>
      <div className={`sioc-input-box ${allowCopy ? 'copy-cursor' : ''}`} style={customInputWidth ? { width: customInputWidth } : {}} onClick={() => allowCopy && handleCopy(value)} title={allowCopy ? "Clique para copiar" : ""}>{value || ''}</div>
    </div>
  );

  const CheckboxSioc = ({ label, checked }) => (
    <div className="checkbox-item"><div className={`checkbox-box ${checked ? 'checked' : ''}`}>{checked ? '✔' : ''}</div><span>{label}</span></div>
  );

  return (
    <div className="processing-page" style={{ background: '#ffffff' }}>
      <Header />
      <main className="processing-main-content">
        <div style={{ width: '100%', maxWidth: '1400px' }}>
          <div className="page-top-header">
            <h1 className="page-top-title">Detalhes do Atendimento</h1>
            <div className="page-top-actions">
              <BotaoDownloadFormularioPDF
                data={data}
                agenteNome={agenteNome}
                municipioNome={municipioNome}
                fotosUrls={parseArray(data.fotos_urls)}
                fotoDenunciaUrl={denuncia?.fotoPrincipalUrl || null}
                denunciaData={denuncia}
                feedbackAgente={denuncia?.feedbackAgente || data.feedback_agente || ''}
                fichaFotosUrls={parseArray(data.fotos_ficha_urls)}
                isReady={agenteNome !== 'Carregando...' && municipioNome !== ''}
              />
              {!data.sincronizado && <button onClick={onStartProcess} className="btn-process-main">PROCESSAR FORMULÁRIO DE CAMPO</button>}
              <button onClick={onClose} className="page-top-close">&times;</button>
            </div>
          </div>

          <div className={`page-content-wrapper ${denuncia ? 'layout-denuncia' : ''}`}>
            <div className="form-main-column">
              <div className="form-content-area">
                <div className="sioc-group-title" style={{ marginTop: 0 }}>Dados da Atividade</div>
                <div className="form-stack">
                  <div className="sioc-field-wrapper"><span className="sioc-label">Tipo de Atividade:</span><div className="checkbox-group-clean"><CheckboxSioc label="Pesquisa" checked={isPesquisa} /><CheckboxSioc label="Borrifação" checked={isBorrifacao} /><CheckboxSioc label="Atendimento ao PIT" checked={isPit} /></div></div>
                  <div className="sioc-field-wrapper"><span className="sioc-label">Data da Atividade</span><div className="date-group"><div className="sioc-input-box date-box">{day}</div><div className="sioc-input-box date-box-wide">{month}</div><div className="sioc-input-box date-box">{year}</div></div></div>
                  <InputSioc label="Número do PIT" value={data.numero_pit} widthClass="w-medium" />
                </div>
                <div className="sioc-group-title">Dados do Domicílio</div>
                <div className="form-stack">
                  <InputSioc label="Código da Localidade" value={data.codigo_localidade} widthClass="w-medium" />
                  <InputSioc label="Nome da Localidade" value={data.nome_localidade} />
                  <InputSioc label="Categoria" value={data.categoria_localidade} widthClass="w-medium" />
                  <InputSioc label="Endereço" value={data.endereco} />
                  <InputSioc label="Número" value={data.numero} widthClass="w-auto" customInputWidth="100px" />
                  <InputSioc label="Complemento" value={data.complemento} />
                  <InputSioc label="Pendência da Pesquisa" value={mapValueToLabel(data.pendencia_pesquisa)} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Pendência da Borrifação" value={mapValueToLabel(data.pendencia_borrifacao)} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Nome do Morador" value={data.nome_morador} />
                  <InputSioc label="Quantidade de Moradores" value={data.quantidade_moradores} widthClass="w-auto" customInputWidth="100px" allowCopy={false} />
                  <InputSioc label="Número Anexo" value={data.numero_anexo} widthClass="w-auto" customInputWidth="80px" allowCopy={false} />
                  <InputSioc label="Situação do Imóvel" value={mapValueToLabel(data.situacao_imovel)} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Tipo de Parede" value={mapValueToLabel(data.tipo_parede)} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Tipo de Teto" value={mapValueToLabel(data.tipo_teto)} widthClass="w-medium" allowCopy={false} />
                  <div className="sioc-field-wrapper"><span className="sioc-label">Melhoria Habitacional</span><div className="checkbox-group-clean"><CheckboxSioc label="1 - Sim" checked={data.melhoria_habitacional === true} /><CheckboxSioc label="2 - Não" checked={data.melhoria_habitacional === false} /></div></div>
                </div>
                <div className="sioc-group-title">Captura de Triatomíneos</div>
                <div className="form-stack">
                  <InputSioc label="Captura Intradomicílio" value={capturaIntra} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Vestígios Intradomicílio" value={data.vestigios_intradomicilio} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Nº de Barbeiros (Intra)" value={data.barbeiros_intradomicilio || '0'} widthClass="w-auto" customInputWidth="80px" allowCopy={false} />
                </div>
                <div className="form-stack">
                  <InputSioc label="Captura Peridomicílio" value={capturaPeri} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Vestígios Peridomicílio" value={data.vestigios_peridomicilio} widthClass="w-medium" allowCopy={false} />
                  <InputSioc label="Nº de Barbeiros (Peri)" value={data.barbeiros_peridomicilio || '0'} widthClass="w-auto" customInputWidth="80px" allowCopy={false} />
                </div>
                <div className="sioc-group-title">Borrifação</div>
                <div className="form-stack">
                  <InputSioc label="Inseticida" value={data.inseticida} widthClass="w-large" allowCopy={false} />
                  <InputSioc label="Número de Cargas" value={data.numero_cargas || "0"} widthClass="w-auto" customInputWidth="100px" allowCopy={false} />
                </div>
                <div className="sioc-group-title">Dados de Identificação</div>
                <div className="form-stack">
                  <InputSioc label="Código de Etiqueta" value={data.codigo_etiqueta} widthClass="w-large" />
                  <InputSioc label="Agente Responsável" value={agenteNome} widthClass="w-large" />
                </div>
                
                {fichaImages.length > 0 && (
                   <div style={{ marginTop: '30px' }}>
                      <button onClick={() => window.open(fichaImages[0], '_blank')} className="btn-view-ficha">VISUALIZAR FOTOS DA FICHA FÍSICA ({fichaImages.length})</button>
                   </div>
                )}
              </div>
            </div>
            {denuncia && (
              <div className="denuncia-context-panel">
                <DenunciaContextCard denuncia={{ ...denuncia, feedbackAgente: denuncia.feedbackAgente || data.feedback_agente || '' }} showFeedback={true} />
              </div>
            )}
            <div className="denuncia-actions-panel">
              <div className="photos-area">
                <h3 className="photos-area-title">Registros do Atendimento</h3>
                {images.length > 0 ? (
                  <div className="photos-grid-2col">
                    {images.map((url, idx) => (
                      <div key={idx} className="photo-card-small relative">
                        <img src={url} alt={`Evidência ${idx + 1}`} />
                        <div className="photo-actions-overlay">
                          <button className="action-btn zoom" onClick={() => window.open(url, '_blank')} title="Ver foto">🔍</button>
                        </div>
                        <span className="photo-label">Foto {idx + 1}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="no-photos-placeholder">Sem registros</div>}
              </div>
            </div>
          </div>
        </div>
        {toastMsg && <div className="toast-notification">{toastMsg}</div>}
      </main>
      <div className="portal-footer-bar">
        <button className="btn-cancel" onClick={onClose}>Fechar</button>
        {!data.sincronizado && <button className="btn-save" style={{ background: '#f97316' }} onClick={onStartProcess}>Processar Formulário de Campo</button>}
      </div>
      <style>{`
        .btn-process-main { padding: 8px 24px; background: #f97316; color: white; border: none; borderRadius: 6px; fontWeight: 700; cursor: pointer; font-size: 14px; }
        .btn-view-ficha { width: 100%; padding: 15px; background: #334155; color: white; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; transition: background 0.2s; }
        .btn-view-ficha:hover { background: #1e293b; }
        .relative { position: relative; }
        .photo-actions-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.2); display: flex; justify-content: center; align-items: center; gap: 10px; opacity: 0; transition: opacity 0.2s; border-radius: 4px; }
        .photo-card-small:hover .photo-actions-overlay, .photo-card-mini:hover .photo-actions-overlay { opacity: 1; }
        .action-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: white; cursor: pointer; display: flex; justify-content: center; align-items: center; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); transition: transform 0.2s; }
        .action-btn:hover { transform: scale(1.1); }
        .action-btn.zoom { color: #3b82f6; }
      `}</style>
    </div>
  );
};

export default ProcessingModal;
